/**
 * BannerImageEditor — two-step wizard for the artist's banner image.
 *
 *   Step 1: Web banner. Artist uploads an image and crops it to the web
 *           aspect ratio (~3:1). Live preview at the rendered web size.
 *   Step 2: Mobile banner. Defaults to the same source image with a center
 *           crop at the mobile aspect ratio (~9:16). Artist can either
 *           adjust the crop region OR click "Use a different image" to
 *           upload a separate mobile source.
 *
 * On save, fires the relevant uploads via the artist's *Data setters
 * (which the store reacts to). Web upload only fires if the web crop was
 * touched in this session; mobile upload only fires if the mobile crop or
 * source was touched. No upload happens for steps the artist didn't visit.
 */

import _ from 'lodash';
import React, { Component } from 'react';
import autobind from 'autobind-decorator';
import { observer } from 'mobx-react';
import Cropper from 'react-cropper';

import { HFMModalNeo } from 'client/shared/components';

const WEB_ASPECT    = 2611.2 / 864; // ~3.02
const MOBILE_ASPECT = 9 / 16;       // 0.5625

const readFileAsDataURL = (file) => new Promise((resolve, reject) => {
	const reader = new FileReader();
	reader.onload  = () => resolve(reader.result);
	reader.onerror = reject;
	reader.readAsDataURL(file);
});

@observer
class BannerImageEditor extends Component {
	state = {
		isOpen: false,
		step: 'web', // 'web' | 'mobile'

		// Web step
		webSrc: '',           // base64 of uploaded source for the web crop
		webCropResult: '',    // base64 of cropped output (preview + payload)
		webTouched: false,

		// Mobile step. Defaults to the web source so the artist gets a
		// reasonable pre-cropped starting point. If they pick a different
		// image, we swap mobileSrc.
		mobileSrc: '',
		mobileCropResult: '',
		mobileTouched: false,
		mobileUsesOwnImage: false,
	};

	webCropperRef    = null;
	mobileCropperRef = null;

	@autobind
	openModal (step = 'web') {
		const { artist } = this.props;
		// Seed the croppers from the existing banner image URLs (when set)
		// so the artist can re-crop without re-uploading. Cropper.js loads
		// the URL via crossOrigin='anonymous' — requires CORS on the asset
		// distribution. If CORS blocks, the cropper renders empty and the
		// artist falls back to uploading a new image (no error, just no seed).
		//
		// We DON'T set *Touched here — touched flips true only when the
		// artist actually interacts (uploads OR drags the crop box).
		this.setState({
			isOpen: true,
			step,
			webSrc: artist.bannerImagePath || '',
			webCropResult: '',
			webTouched: false,
			mobileSrc: artist.mobileBannerImagePath || artist.bannerImagePath || '',
			mobileCropResult: '',
			mobileTouched: false,
			mobileUsesOwnImage: !!artist.mobileBannerImagePath,
		});
	}

	@autobind
	openModalToWeb ()    { this.openModal('web'); }
	@autobind
	openModalToMobile () { this.openModal('mobile'); }

	@autobind
	closeModal () {
		this.setState({ isOpen: false });
	}

	@autobind
	async onWebFileChange (evt) {
		const file = _.first(evt.target?.files);
		if (!file) return;

		const dataURL = await readFileAsDataURL(file);
		const usingOwnMobile = this.state.mobileUsesOwnImage;
		this.setState({
			webSrc: dataURL,
			// Auto-seed the mobile source unless the artist has chosen a
			// dedicated image. This way, switching to step 2 lands on a
			// pre-cropped center frame of the same image.
			mobileSrc: usingOwnMobile ? this.state.mobileSrc : dataURL,
			webTouched: true,
			// Mark mobile as implicitly touched too — the auto-derived crop
			// IS what the artist wants saved by default. Without this, hitting
			// Save without explicitly dragging the mobile crop would skip the
			// mobile upload entirely, leaving the field empty and the mobile
			// app falling back to the wide web banner (which `cover`-crops
			// into a portrait container with extreme zoom).
			mobileTouched: usingOwnMobile ? this.state.mobileTouched : true,
		});
	}

	@autobind
	async onMobileFileChange (evt) {
		const file = _.first(evt.target?.files);
		if (!file) return;

		const dataURL = await readFileAsDataURL(file);
		this.setState({
			mobileSrc: dataURL,
			mobileUsesOwnImage: true,
			mobileTouched: true,
		});
	}

	@autobind
	captureCrop (which) {
		const ref = which === 'web' ? this.webCropperRef : this.mobileCropperRef;
		if (!ref) return '';

		const canvas = ref.getCroppedCanvas(
			which === 'web'
				? { width: 1280, height: Math.round(1280 / WEB_ASPECT) }
				: { width: 720,  height: Math.round(720 / MOBILE_ASPECT) }
		);
		if (!canvas) return '';

		// Default toDataURL() emits PNG (lossless, huge). JPEG @ 0.85 is
		// visually indistinguishable for a banner photo and ~5-10× smaller.
		// The mobile crop hit 413 on PNG because of the larger portrait
		// pixel area; JPEG keeps both well under nginx/koa body limits.
		return canvas.toDataURL('image/jpeg', 0.85);
	}

	@autobind
	goToMobileStep () {
		// Save the web banner NOW (before unmounting the web cropper). One
		// network call per click — no race with a later mobile save.
		const cropResult = this.state.webTouched
			? this.captureCrop('web')
			: this.state.webCropResult;

		if (this.state.webTouched && cropResult) {
			// Setter on the artist model triggers store.saveBannerImage(...)
			// which fires the POST. We mark webTouched=false after so a
			// second pass through Next doesn't re-upload an unchanged crop.
			this.props.artist.bannerImageData = cropResult;
		}

		this.setState({
			step: 'mobile',
			webCropResult: cropResult,
			webTouched: false,
		});
	}

	@autobind
	backToWebStep () {
		// No save here — we save mobile only on the explicit Save click.
		// Cache the latest mobile crop so the preview survives the cropper
		// unmounting when we render the web step.
		const cropResult = this.state.mobileTouched
			? this.captureCrop('mobile')
			: this.state.mobileCropResult;
		this.setState({ step: 'web', mobileCropResult: cropResult });
	}

	@autobind
	onSaveWeb () {
		const webData = this.state.webTouched
			? (this.captureCrop('web') || this.state.webCropResult)
			: this.state.webCropResult;

		if (webData && this.state.webTouched) {
			this.props.artist.bannerImageData = webData;
		}

		this.closeModal();
	}

	@autobind
	onSaveMobile () {
		// Mobile cropper IS mounted (we're on the mobile step). Capture live,
		// fall back to cached if live capture returns nothing.
		const mobileData = this.state.mobileTouched
			? (this.captureCrop('mobile') || this.state.mobileCropResult)
			: this.state.mobileCropResult;

		if (mobileData && this.state.mobileTouched) {
			this.props.artist.mobileBannerImageData = mobileData;
		}

		this.closeModal();
	}

	renderTriggerThumb () {
		const { artist } = this.props;
		const webSrc    = artist.bannerImagePath;
		const mobileSrc = artist.mobileBannerImagePath;

		return (
			<div className="banner-image-editor__triggers">
				<div className="banner-image-editor__thumb-row">
					{/* Each thumb is its own entry point — click web to edit
					    the web banner, click mobile to jump straight to the
					    mobile step. `.edit-image` gives the same hover-"Edit"
					    overlay used by the profile image, for consistency. */}
					<div
						className="banner-image-editor__thumb banner-image-editor__thumb--web edit-image"
						style={{ backgroundImage: webSrc ? `url("${webSrc}")` : 'none' }}
						title="Edit web banner"
						onClick={ this.openModalToWeb }
					>
						{ !webSrc && (
							<span className="banner-image-editor__thumb-placeholder">No web banner yet</span>
						) }
					</div>
					<div
						className="banner-image-editor__thumb banner-image-editor__thumb--mobile edit-image"
						style={{ backgroundImage: mobileSrc ? `url("${mobileSrc}")` : 'none' }}
						title="Edit mobile banner"
						onClick={ this.openModalToMobile }
					>
						{ !mobileSrc && (
							<span className="banner-image-editor__thumb-placeholder">No mobile banner yet</span>
						) }
					</div>
				</div>
			</div>
		);
	}

	// Live crop preview — re-renders on every cropmove so the artist sees
	// the result mirrored in the right-hand preview frame.
	@autobind
	onCropChange (which) {
		const result = this.captureCrop(which);
		if (!result) return;
		if (which === 'web') {
			this.setState({ webCropResult: result, webTouched: true });
		} else {
			this.setState({ mobileCropResult: result, mobileTouched: true });
		}
	}

	renderWebStep () {
		const { artist } = this.props;
		const { webSrc, webCropResult } = this.state;
		const previewBg = webCropResult || (webSrc ? webSrc : artist.bannerImagePath);

		return (
			<div className="banner-image-editor__step">
				<h3>Web banner</h3>
				<p className="banner-image-editor__hint">
					This is the wide banner shown on the website. We recommend an
					image at least 2000×1000.
				</p>

				<div className="banner-image-editor__body banner-image-editor__body--web">
					<div className="banner-image-editor__crop">
						<h4>Crop</h4>
						<div className="banner-image-editor__crop-frame banner-image-editor__crop-frame--web">
							{ webSrc ? (
								<Cropper
									style={{ width: '100%', height: '100%' }}
									aspectRatio={ WEB_ASPECT }
									src={ webSrc }
									guides={ false }
									crossOrigin="anonymous"
									cropend={ () => this.onCropChange('web') }
									ref={ (c) => { this.webCropperRef = c; } }
								/>
							) : (
								<div className="banner-image-editor__empty">
									Choose an image to begin.
								</div>
							) }
						</div>
					</div>

					<div className="banner-image-editor__preview-col">
						<h4>Web preview</h4>
						<div
							className="banner-image-editor__preview banner-image-editor__preview--web"
							style={{
								backgroundImage: previewBg ? `url(${previewBg})` : 'none',
							}}
						>
							<div className="banner-image-editor__preview-label">
								{ (artist.name || 'Artist Name').toUpperCase() }
							</div>
						</div>
					</div>
				</div>

				<div className="banner-image-editor__actions">
					<label htmlFor="webBannerUpload" className="button minor">
						{ webSrc ? 'Choose Different Image' : 'Choose Image' }
					</label>
					<input
						type="file"
						accept=".png, .jpg, .jpeg"
						id="webBannerUpload"
						className="show-for-sr"
						onChange={ this.onWebFileChange }
					/>

					<button className="button neo" type="button" onClick={ this.closeModal }>
						Cancel
					</button>

					<button
						className="button neo"
						type="button"
						onClick={ this.onSaveWeb }
						disabled={ !this.state.webTouched }
					>
						Save
					</button>
				</div>
			</div>
		);
	}

	renderMobileStep () {
		const { artist } = this.props;
		const { mobileSrc, mobileCropResult, mobileUsesOwnImage } = this.state;
		const previewBg = mobileCropResult || (mobileSrc ? mobileSrc : artist.mobileBannerImagePath);

		return (
			<div className="banner-image-editor__step">
				<h3>Mobile banner</h3>
				<p className="banner-image-editor__hint">
					This is the tall banner shown in the mobile app. By default it
					uses the same image as the web banner, center-cropped to
					portrait. You can drag to adjust the crop, or upload a
					completely different image just for mobile.
				</p>

				<div className="banner-image-editor__body banner-image-editor__body--mobile">
					<div className="banner-image-editor__crop">
						<h4>Crop</h4>
						<div className="banner-image-editor__crop-frame banner-image-editor__crop-frame--mobile">
							{ mobileSrc ? (
								<Cropper
									style={{ width: '100%', height: '100%' }}
									aspectRatio={ MOBILE_ASPECT }
									src={ mobileSrc }
									guides={ false }
									crossOrigin="anonymous"
									cropend={ () => this.onCropChange('mobile') }
									// `key` forces remount when the source swaps so the
									// cropper resets its internal crop box.
									key={ mobileSrc.slice(0, 64) }
									ref={ (c) => { this.mobileCropperRef = c; } }
								/>
							) : (
								<div className="banner-image-editor__empty">
									Choose an image below — or click "Back to Web" to pick a web banner first and have it auto-derived.
								</div>
							) }
						</div>
					</div>

					<div className="banner-image-editor__preview-col">
						<h4>Mobile preview</h4>
						<div
							className="banner-image-editor__preview banner-image-editor__preview--mobile"
							style={{
								backgroundImage: previewBg ? `url(${previewBg})` : 'none',
							}}
						>
							<div className="banner-image-editor__preview-label">
								{ artist.name || 'Artist Name' }
							</div>
						</div>
					</div>
				</div>

				<div className="banner-image-editor__actions">
					<label htmlFor="mobileBannerUpload" className="button minor">
						{ mobileUsesOwnImage ? 'Replace Mobile Image' : 'Use a Different Image for Mobile' }
					</label>
					<input
						type="file"
						accept=".png, .jpg, .jpeg"
						id="mobileBannerUpload"
						className="show-for-sr"
						onChange={ this.onMobileFileChange }
					/>

					<button className="button neo" type="button" onClick={ this.closeModal }>
						Cancel
					</button>

					<button
						className="button neo"
						type="button"
						onClick={ this.onSaveMobile }
						disabled={ !this.state.mobileTouched }
					>
						Save
					</button>
				</div>
			</div>
		);
	}

	render () {
		const { isOpen, step } = this.state;
		// The step modifier flows up to .modal-cms via :has() so the modal
		// width can shrink for the portrait mobile step.
		const rootClass = `banner-image-editor banner-image-editor--${step}`;

		return (
			<>
				{ this.renderTriggerThumb() }

				<HFMModalNeo isOpen={ isOpen } contentLabel="Edit Banner Images">
					<div className={ rootClass }>
						{ step === 'web' ? this.renderWebStep() : this.renderMobileStep() }
					</div>
				</HFMModalNeo>
			</>
		);
	}
}

export default BannerImageEditor;
